import constant from "@/utils/constant";
import { useEffect } from "react";
import { create } from "zustand";
import * as Sentry from "@sentry/react";

export const useUpdateStatus = create<{
    newVersion: boolean;
    canceled: boolean;
    setCanceled(canceled: boolean): void;
    setNewVersion(newVersion: boolean): void;
}>(set => ({
    newVersion: false,
    canceled: false,
    setNewVersion: newVersion => set({ newVersion }),
    setCanceled: canceled => set({ canceled }),
}));

const useUpdates = () => {
    const setNewVersion = useUpdateStatus(state => state.setNewVersion);

    const checkUpdates = async () => {
        try {
            const { canceled, newVersion } = useUpdateStatus.getState();
            if (canceled || newVersion) return;

            const deploymentURL = constant.PROD
                ? "https://portal.dippi.app/index.html"
                : "https://portal-staging.dippi.app/index.html";

            const res = await fetch(deploymentURL, { mode: "cors", cache: "no-store" });
            const html = await res.text();

            const remoteMainScript = /.*<script.*(index-[A-Z\d]+\.js).*><\/script>.*/gi.exec(
                html,
            )?.[1];

            if (!remoteMainScript) return;

            let currentMainScript: string | null = null;
            const currentScripts = document.head.getElementsByTagName("script");

            for (const script of currentScripts) {
                const extractedScript = /.*(index-[A-Z\d]+\.js).*/gi.exec(script.src)?.[1];

                if (extractedScript) {
                    currentMainScript = extractedScript;
                    break;
                }
            }

            if (remoteMainScript === currentMainScript) setNewVersion(false);
            if (remoteMainScript !== currentMainScript) {
                Sentry.captureEvent({
                    level: "info",
                    message: `User has received an update while using portal. \nremoteMainScript:${remoteMainScript} \ncurrentMainScript: ${currentMainScript}`,
                });
                setNewVersion(true);
            }
        } catch (e) {
            null;
        }
    };

    useEffect(() => {
        if (constant.DEV) return;

        const interval = setInterval(checkUpdates, 60000 * 2);
        return () => clearInterval(interval);
    }, []);
};

export default useUpdates;
